import { useCallback, useMemo } from 'react';
import { useUseCelo } from '../providers/UseCeloProvider';

/**
 * Enhanced hook that provides all pricing functionality with additional convenience methods
 * This is a convenience wrapper around useUseCelo with additional helper methods
 */
export function usePricing() {
  const context = useUseCelo();

  // Convenience method: Sync check for feature access
  const hasFeature = useCallback((featureTag: string): boolean => {
    return context.hasFeature(featureTag);
  }, [context]);

  // Get feature info
  const getFeature = useCallback((featureTag: string) => {
    return context.getFeature(featureTag);
  }, [context]);

  // Get usage info for a specific feature
  const getUsage = useCallback((featureTag: string): number | null => {
    const feature = context.getFeature(featureTag);
    return typeof feature?.usage === 'number' ? feature.usage : null;
  }, [context]);

  // Check if user is currently in trial (basic check based on subscription status)
  const isTrialing = useMemo(() => {
    return context.subscription?.status === 'trialing';
  }, [context.subscription]);

  // Check if user has an active subscription
  const isSubscribed = useMemo(() => {
    return context.subscription?.status === 'active' || context.subscription?.status === 'trialing';
  }, [context.subscription]);

  // Generate upgrade URL (basic implementation)
  const upgradeUrl = useMemo(() => {
    const baseUrl = context.apiHost?.replace('/api', '') || 'https://usecelo.com';
    return `${baseUrl}/upgrade?appId=${context.appId}`;
  }, [context.apiHost, context.appId]);

  // Generate billing/customer portal URL
  const billingUrl = useMemo(() => {
    const baseUrl = context.apiHost?.replace('/api', '') || 'https://usecelo.com';
    return `${baseUrl}/billing?appId=${context.appId}`;
  }, [context.apiHost, context.appId]);

  // Enhanced trackUsage with basic validation
  const trackUsageWithCheck = useCallback(async (
    featureTag: string,
    amount: number = 1,
    options: {
      checkBeforeTrack?: boolean;
      throwOnLimitExceeded?: boolean;
    } = {}
  ) => {
    const { checkBeforeTrack = true, throwOnLimitExceeded = true } = options;

    try {
      // Basic access check
      if (checkBeforeTrack && !hasFeature(featureTag)) {
        const error = new Error(`Feature '${featureTag}' is not accessible`);
        if (throwOnLimitExceeded) throw error;
        return { success: false, reason: 'no_access', error: error.message };
      }

      await context.trackSingleUsage(featureTag, amount);

      return { success: true, usage: getUsage(featureTag) };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (throwOnLimitExceeded) throw error;
      return {
        success: false,
        reason: 'tracking_failed',
        error: errorMessage
      };
    }
  }, [context, hasFeature, getUsage]);

  // Get feature access for multiple features
  const checkMultipleFeatures = useCallback(async (featureTags: string[], requireAll: boolean = true) => {
    const results = featureTags.reduce((acc, tag) => {
      const feature = context.getFeature(tag);
      acc[tag] = {
        hasAccess: feature?.hasAccess || false,
        feature,
        config: feature?.config,
        limits: feature?.limits,
        reason: feature?.reason
      };
      return acc;
    }, {} as Record<string, any>);

    const hasAccess = requireAll
      ? featureTags.every(tag => results[tag].hasAccess)
      : featureTags.some(tag => results[tag].hasAccess);

    return { hasAccess, results };
  }, [context]);

  // Get feature with usage info
  const getFeatureWithUsage = useCallback(async (featureTag: string) => {
    const feature = context.getFeature(featureTag);
    const usage = getUsage(featureTag);

    return {
      hasAccess: feature?.hasAccess || false,
      feature,
      config: feature?.config,
      limits: feature?.limits,
      reason: feature?.reason,
      usage,
      name: featureTag,
      tag: featureTag
    };
  }, [context, getUsage]);

  return {
    // All original context methods and properties
    ...context,

    // Additional convenience methods
    hasFeature,
    getFeature,
    getUsage,
    isTrialing,
    isSubscribed,
    upgradeUrl,
    billingUrl,

    // Enhanced methods
    trackUsageWithCheck,
    checkMultipleFeatures,
    getFeatureWithUsage
  };
}

// Specialized hooks for specific use cases

/**
 * Hook specifically for feature checking with caching
 */
export function useFeature(featureTag: string) {
  const { getFeature, hasFeature, loading } = useUseCelo();

  const feature = getFeature(featureTag);

  return {
    hasAccess: hasFeature(featureTag),
    feature,
    config: feature?.config,
    limits: feature?.limits,
    usage: feature?.usage,
    loading
  };
}

/**
 * Hook for subscription status
 */
export function useSubscription() {
  const { subscription, loading, refreshFeatures } = useUseCelo();

  return {
    subscription,
    isTrialing: subscription?.status === 'trialing',
    isSubscribed: subscription?.status === 'active' || subscription?.status === 'trialing',
    loading,
    refresh: refreshFeatures
  };
}

/**
 * Hook for usage tracking and monitoring
 */
export function useUsage() {
  const { trackSingleUsage, trackUsage, getFeature, features } = useUseCelo();

  const getAllUsage = useCallback(() => {
    return features.reduce((acc, feature) => {
      if (typeof feature.usage === 'number') {
        acc[feature.tag] = feature.usage;
      }
      return acc;
    }, {} as Record<string, number>);
  }, [features]);

  return {
    trackUsage: trackSingleUsage,
    trackBatchUsage: trackUsage,
    getUsage: (featureTag: string) => {
      const feature = getFeature(featureTag);
      return typeof feature?.usage === 'number' ? feature.usage : null;
    },
    getAllUsage
  };
}